import { Pin } from '@/components/Icons/Pin';
import { TileHoverOverlayButton } from '@/components/TileHoverOverlayButton';
import { cn } from '@/lib/utils';
import type { BoardAssetImage } from '@/open-api/model';
import { Skeleton } from '@music-vine/cadence/ui';
import { cva } from 'class-variance-authority';
import Image from 'next/image';
import Link from 'next/link';
import { type RefObject, useMemo, useState } from 'react';
import { BoardTileMenu } from './partials/BoardPageTileMenu';
import { getBoardCover } from './utils/get-board-cover';
const MAX_IMAGES_PER_COVER = 3;
const tileAspectRatio = cva('aspect-[316/176]');
const imageAssetContainer = cva('relative h-full overflow-hidden border-[0.3rem] border-white border-solid bg-gray-50 dark:border-white', {
  variants: {
    type: {
      Music: 'aspect-square rounded-full',
      Sfx: 'aspect-square rounded-full',
      MotionGraphics: 'aspect-video rounded-lg',
      Video: 'aspect-video rounded-lg'
    },
    featured: {
      true: 'max-h-36',
      false: 'max-h-16'
    }
  },
  defaultVariants: {
    type: 'Music',
    featured: true
  }
});
const BoardTileImageFavorites = () => {
  const [isHovered, setIsHovered] = useState(false);
  return <Link href="/my/boards/favorites" onMouseEnter={() => {
    setIsHovered(true);
  }} onMouseLeave={() => {
    setIsHovered(false);
  }} data-sentry-element="Link" data-sentry-component="BoardTileImageFavorites" data-sentry-source-file="tile.tsx">
      <div className={cn(tileAspectRatio(), 'relative flex items-center justify-center overflow-hidden rounded-lg')}>
        <Image alt={'board tile cover image'} className="absolute object-cover" fill sizes="316px" src={getBoardCover({
        boardIndex: 0
      })} data-sentry-element="Image" data-sentry-source-file="tile.tsx" />
        <div className={cn('relative flex items-center justify-center')}>
          <svg aria-hidden={true} fill="none" height="60" viewBox="0 0 60 60" width="60" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-source-file="tile.tsx">
            <path d="M28.5474 51.667C29.4549 52.382 30.7474 52.382 31.6549 51.662C38.6374 46.127 45.1624 39.832 51.1699 32.6745C56.3049 26.477 56.2774 17.587 50.7974 11.977C47.2474 8.34196 42.4849 7.46696 37.9824 8.40946C35.1649 8.99696 33.0574 10.5145 31.7199 11.7695C30.7499 12.682 29.2599 12.6295 28.2824 11.722C27.1324 10.6545 25.4349 9.42196 23.2399 8.77696C18.3874 7.34946 13.1174 8.02196 9.25494 11.977C3.77494 17.587 4.19494 26.8595 8.88244 32.6745C15.2874 40.4045 21.8774 46.412 28.5474 51.667Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="5" data-sentry-element="path" data-sentry-source-file="tile.tsx" />
          </svg>
        </div>
        <TileHoverOverlayButton show={isHovered} label="View board" data-sentry-element="TileHoverOverlayButton" data-sentry-source-file="tile.tsx" />
      </div>
    </Link>;
};
const BoardTileImagesLoading = () => <div className={cn(tileAspectRatio(), 'relative flex items-center justify-center overflow-hidden rounded-lg', 'bg-gray-50 dark:bg-gray-800')} data-sentry-component="BoardTileImagesLoading" data-sentry-source-file="tile.tsx">
    <Skeleton data-sentry-element="Skeleton" data-sentry-source-file="tile.tsx" />
  </div>;
const BoardTileImages = ({
  boardId,
  boardIndex,
  assetImageUrls
}: {
  boardId: number;
  boardIndex?: number;
  assetImageUrls: BoardAssetImage[];
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const data = assetImageUrls.filter((item): item is NonNullable<typeof item> => !!item?.imageUrl?.length).slice(0, MAX_IMAGES_PER_COVER);
  const secondaryCoverImages = useMemo(() => data.slice(1), [data]);
  if (!data || data.length === 0) {
    return <Link href={`/my/boards/${boardId}`} onMouseEnter={() => {
      setIsHovered(true);
    }} onMouseLeave={() => {
      setIsHovered(false);
    }}>
        <div className={cn(tileAspectRatio(), 'relative flex items-center justify-center overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-800')}>
          <TileHoverOverlayButton show={isHovered} label="View board" />
        </div>
      </Link>;
  }
  return <Link href={`/my/boards/${boardId}`} onMouseEnter={() => {
    setIsHovered(true);
  }} onMouseLeave={() => {
    setIsHovered(false);
  }} data-sentry-element="Link" data-sentry-component="BoardTileImages" data-sentry-source-file="tile.tsx">
      <div className={cn(tileAspectRatio(), 'relative flex items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800')}>
        <Image alt={'board tile cover image'} className="absolute object-cover" fill sizes="316px" src={getBoardCover({
        boardIndex
      })} data-sentry-element="Image" data-sentry-source-file="tile.tsx" />
        {data.length === 1 ? <div className="flex h-full w-full items-center justify-center p-6">
            <div className={imageAssetContainer({
          type: data[0]?.assetType ?? 'Music'
        })}>
              {!!data[0]?.imageUrl && <Image alt="Board tile image" className="object-cover" fill sizes="316px" src={data[0].imageUrl} />}
            </div>
          </div> : <div className="flex h-full w-full flex-row items-center justify-center gap-6 p-6">
            <div className={imageAssetContainer({
          type: data[0]?.assetType ?? 'Music'
        })}>
              {!!data[0]?.imageUrl && <Image alt="Board tile image" className="object-cover" fill sizes="316px" src={data[0].imageUrl} />}
            </div>
            {secondaryCoverImages.length ? <div className="flex h-full flex-col items-center justify-center gap-2">
                {secondaryCoverImages.map(image => {
            if (!image?.imageUrl) {
              return null;
            }
            return <div className={imageAssetContainer({
              type: image.assetType,
              featured: false
            })} key={image.imageUrl}>
                      <Image alt="Board tile image" className="object-cover" fill sizes="176px" src={image.imageUrl} />
                    </div>;
          })}
              </div> : null}
          </div>}
        <TileHoverOverlayButton show={isHovered} label="View board" data-sentry-element="TileHoverOverlayButton" data-sentry-source-file="tile.tsx" />
      </div>
    </Link>;
};
export interface BoardTileProps {
  assetImageUrls: BoardAssetImage[];
  boardIndex?: number;
  id: number;
  isFavoritesBoard: boolean;
  isPinned?: boolean;
  name: string;
}
const BoardTileName = ({
  name,
  isPinned
}: {
  name: string;
  isPinned?: boolean;
}) => <div className="flex w-full min-w-0 items-center gap-1.5 pl-0.5" data-sentry-component="BoardTileName" data-sentry-source-file="tile.tsx">
    {!!isPinned && <Pin className="size-4 min-w-4" />}
    <div className="truncate font-semibold text-lg dark:text-white">{name}</div>
  </div>;
export const BoardTile = ({
  id,
  name,
  isFavoritesBoard,
  isPinned,
  assetImageUrls,
  boardIndex
}: BoardTileProps) => <div className="flex aspect-[380/260] min-h-32 xxs:min-h-64 w-full xxs:min-w-72 flex-1 flex-col gap-1" data-sentry-component="BoardTile" data-sentry-source-file="tile.tsx">
    {isFavoritesBoard ? <BoardTileImageFavorites /> : <BoardTileImages assetImageUrls={assetImageUrls} boardId={id} boardIndex={boardIndex} />}
    <div className="flex w-full items-center justify-between gap-2">
      <BoardTileName isPinned={isPinned} name={name} data-sentry-element="BoardTileName" data-sentry-source-file="tile.tsx" />
      {!isFavoritesBoard && <BoardTileMenu boardId={id} boardName={name} />}
    </div>
  </div>;
export const BoardTileLoading = ({
  ref
}: {
  ref?: RefObject<HTMLDivElement | null>;
}) => <div className="flex aspect-[380/260] min-h-32 xxs:min-h-64 w-full xxs:min-w-72 flex-1 flex-col gap-1" ref={ref} data-sentry-component="BoardTileLoading" data-sentry-source-file="tile.tsx">
    <BoardTileImagesLoading data-sentry-element="BoardTileImagesLoading" data-sentry-source-file="tile.tsx" />
  </div>;